import { createSlice } from "@reduxjs/toolkit";
import * as serverSide from "../server_side";

const initialState = {
  filterName: null,
  currentPage: 1,
  productColumns: 4,
  productRows: 3,
  pagesVisible: 1,
  viewMode: "grid",
  productsPath: null,
  selectInRevitUrl: null,
  channel: null,
  generatingEntries: false,
  generatingEntriesFailed: false,
  generateEntriesError: null,
  downloading: false,
  filters: [],
  blockingUI: false,
  products: [],
  selectedProductIds: [],
  checkUncheckAllState: "check all",
  previewActive: false,
  previewKey: "preview_first",
  basket: {
    open: false,
    products: [],
    addingProductIds: [],
    removingProductIds: [],
    downloading: false
  },
  showLinkToProductPageInProductMenu: undefined,
  translations: {},
  customTranslations: {},
  locale: "en"
};

const defaultSlice = createSlice({
  name: 'default',
  initialState,
  reducers: {
    initialDataLoaded(state, action){
      state.filterId = action.payload.filter_id;
      state.filterName = action.payload.filter_name;
      state.filters = action.payload.filters;
      state.products = action.payload.products;
      state.productColumns = action.payload.product_columns;
      state.productRows = action.payload.product_rows;
      state.downloadPaths = action.payload.download_paths;
      state.selectInRevitUrl = action.payload.select_in_revit_url;
      state.channel = action.payload.channel;
      state.availableDownloadOptions = action.payload.available_download_options;
      state.productsPath = action.payload.products_path;
      state.basket.products = action.payload.basket_products;
      state.showLinkToProductPageInProductMenu =
        action.payload.show_link_to_product_page_in_product_menu;
      state.translations = action.payload.translations;
      state.customTranslations = action.payload.custom_translations;
      state.locale = action.payload.locale;
    },
    selectFilterChanged(state, action){
      const filters = state.filters.map(filter => {
        if (filter.key == action.payload.key) {
          const value = action.payload.values;
          return { ...filter, value };
        }
        return filter;
      });
      state.filters = filters;
    },
    minInputChanged(state, action){
      const filters = state.filters.map(filter => {
        if (filter.key == action.payload.key) {
          const minValue = action.payload.value;
          return { ...filter, minValue };
        }
        return filter;
      });
      state.filters = filters;
    },
    maxInputChanged(state, action){
      const filters = state.filters.map(filter => {
        if (filter.key == action.payload.key) {
          const maxValue = action.payload.value;
          return { ...filter, maxValue };
        }
        return filter;
      });
      state.filters = filters;
    },
    fetchFilterStateRequested(state){
      state.blockingUI = true;
    },
    filterStateFetched(state, action){
      state.blockingUI = false;
      state.currentPage = 1;
      state.filters = action.payload.filters;
      state.products = action.payload.products;
    },
    failedToFetchFilterState(state){
      state.blockingUI = false;
    },
    resetFilters(state){
      const filters = state.filters.map(filter => {
        if (filter.showAsInterval || filter.showAsMinText) {
          const minValue = "";
          return { ...filter, minValue };
        } else if (filter.showAsInterval || filter.showAsMaxText) {
          const maxValue = "";
          return { ...filter, maxValue };
        } else {
          const value = undefined;
          return { ...filter, value };
        }
      });
      state.filters = filters;
    },
    checkAllProducts(state){
      const selectedProductIds = state.products.map(p => p.id);
      const checkUncheckAllState = "uncheck all";
      state.selectedProductIds = selectedProductIds;
      state.checkUncheckAllState = checkUncheckAllState;
    },
    uncheckAllProducts(state){
      state.selectedProductIds = [];
      state.checkUncheckAllState = "check all";
    },
    viewModeChanged(state, action){
      state.viewMode = action.payload;
    },
    loadNextPage(state){
      state.pagesVisible += 1;
    },
    pageClicked(state, action){
      state.currentPage = action.payload;
    },
    productPhotoClicked(state, action){
      const productId = action.payload;
      let selectedProductIds;
      if (state.selectedProductIds.includes(productId)) {
        selectedProductIds = state.selectedProductIds.filter(
          id => id != productId
        );
      } else {
        selectedProductIds = state.selectedProductIds.concat([productId]);
      }
      state.selectedProductIds = selectedProductIds;
    },
    downloadRequested(state){
      state.downloading = true;
    },
    closeDownloadNotification(state){
      state.downloading = false;
    },
    basketDownloadRequested(state){
      state.basket.downloading = true;
    },
    closeBasketDownloadNotification(state){
      state.basket.downloading = false;
    },
    openBasket(state){
      state.basket.open = true;
    },
    closeBasket(state){
      state.basket.open = false;
    },

    addProductRequested(state, action){
      const productId = action.payload;
      const addingProductIds = state.basket.addingProductIds.concat([productId]);
      state.basket.addingProductIds = addingProductIds;
    },
    productAdded(state, action){
      const product = action.payload;
      const products = state.basket.products.concat([product]);
      const addingProductIds = state.basket.addingProductIds.filter(
        pId => pId !== product.id
      );
      state.basket.products = products;
      state.basket.addingProductIds = addingProductIds;
    },
    productNotAdded(state, action){
      const productId = action.payload;
      const addingProductIds = state.basket.addingProductIds.filter(
        pId => pId !== productId
      );
      state.basket.addingProductIds = addingProductIds;
    },
    failedToAddProduct(state, action){
      const productId = action.payload;
      const addingProductIds = state.basket.addingProductIds.filter(
        pId => pId !== productId
      );
      state.basket.addingProductIds = addingProductIds;
    },

    removeProductRequested(state, action){
      const productId = action.payload;
      const removingProductIds = state.basket.removingProductIds.concat([productId]);
      state.basket.removingProductIds = removingProductIds;
    },
    productRemoved(state, action){
      const productId  = action.payload.id;
      const products = state.basket.products.filter(
        product => product.id != productId
      );
      const removingProductIds = state.basket.removingProductIds.filter(
        pId => pId !== productId
      );
      state.basket.products = products;
      state.basket.removingProductIds = removingProductIds;
    },
    productNotRemoved(state, action){
      const productId = action.payload.id;
      const removingProductIds = state.basket.removingProductIds.filter(
        pId => pId !== productId
      );
      state.basket.removingProductIds = removingProductIds;
    },
    failedToRemoveProduct(state, action){
      const productId = action.payload.id;
      const removingProductIds = state.basket.removingProductIds.filter(
        pId => pId !== productId
      );
      state.basket.removingProductIds = removingProductIds;
    },

    generateEntriesRequested(state){
      state.generatingEntriesFailed = false;
      state.generatingEntries = true;
      state.generateEntriesError = null;
    },
    entriesGenerated(state){
      state.generatingEntries = false;
    },
    failedToGenerateEntries(state, action){
      state.generateEntriesError = action.payload;
      state.generatingEntries = false;
      state.generatingEntriesFailed = true;
    },

    activatePreview(state){
      state.previewActive = true;
    },
    hidePreview(state){
      state.previewActive = false;
      state.previewKey = "preview_" + Date.now();
    }
  }
});

export const {
  initialDataLoaded,
  selectFilterChanged,
  minInputChanged,
  maxInputChanged,

  fetchFilterStateRequested,
  filterStateFetched,
  failedToFetchFilterState,

  resetFilters,
  checkAllProducts,
  uncheckAllProducts,
  viewModeChanged,
  loadNextPage,
  pageClicked,
  productPhotoClicked,
  downloadRequested,

  closeDownloadNotification,
  basketDownloadRequested,
  closeBasketDownloadNotification,
  openBasket,
  closeBasket,

  addProductRequested,
  productAdded,
  productNotAdded,
  failedToAddProduct,

  removeProductRequested,
  productRemoved,
  productNotRemoved,
  failedToRemoveProduct,

  generateEntriesRequested,
  entriesGenerated,
  failedToGenerateEntries,

  activatePreview,
  hidePreview
} = defaultSlice.actions;

export const defaultReducer = defaultSlice.reducer;

export const fetchFilterState = () => {
  return (dispatch, getState) => {
    dispatch(fetchFilterStateRequested());
    const { filterId, filters, locale } = getState().default;
    return serverSide.fetchFilterState(filterId, filters, locale).then(
      filterState => {
        dispatch(filterStateFetched(filterState));
      },
      () => {
        dispatch(failedToFetchFilterState());
      }
    );
  };
};

export const addProduct = productId => {
  return dispatch => {
    dispatch(addProductRequested(productId));
    return serverSide.addProduct(productId).then(
      (product, textStatus) => {
        if (textStatus === "notmodified") {
          dispatch(productNotAdded(productId));
        } else {
          dispatch(productAdded(product));
        }
      },
      () => {
        dispatch(failedToAddProduct(productId));
      }
    );
  };
};

export const removeProduct = productId => {
  return dispatch => {
    dispatch(removeProductRequested(productId));
    return serverSide.removeProduct(productId).then(
      (product, textStatus) => {
        if (textStatus === "notmodified") {
          dispatch(productNotRemoved(productId));
        } else {
          dispatch(productRemoved(product));
        }
      },
      () => {
        dispatch(failedToRemoveProduct(productId));
      }
    );
  };
};

export const generateEntries = (selectInRevitUrl, channel, productIds) => {
  return dispatch => {
    dispatch(generateEntriesRequested());
    return serverSide.generateEntries(selectInRevitUrl, channel, productIds).then(
      () => {
        dispatch(entriesGenerated());
      },
      (jqXHR) => {
        const defaultError = "Failed to generate entries",
              responseError = jqXHR.responseJSON && jqXHR.responseJSON["error"],
              error = responseError || defaultError;

        dispatch(failedToGenerateEntries(error));
      }
    );
  };
};